import { getHistoryData } from "../../data/ramotswe";
import {
  getCurrentObservatoryName,
  changeBinnedTimeSeriesUTCTimeToLocalTime,
  changeTimeSeriesUTCTimeToLocalTime,
  createNullBinnedTimeSeriesData,
  getXLims,
  getBinnedXValues,
  getWidestXLimits,
  ensureYKeyPresence,
  trimNullEdges,
} from "../../utils";
import { DashboardTimeLineGraph } from "../../graphs/base_graphs";
import { DateTime } from "../../imports";

export default async function initializeSolarEnergy() {
  // define how the data is fetched
  async function getData() {
    const observatoryName = getCurrentObservatoryName();
    const endDateTime = DateTime.now().setZone("UTC");
    const startDateTime = endDateTime.minus({ hours: 18 });

    let solarChargeRateData = await getHistoryData(
      observatoryName,
      "solar/solar_charge_rate",
      startDateTime,
      endDateTime
    );
    if (solarChargeRateData === null) {
      solarChargeRateData = createNullBinnedTimeSeriesData(
        "solar_charge_rate",
        startDateTime,
        endDateTime
      );
    }

    let solarAltitudeData = await getHistoryData(
      observatoryName,
      "telemetrytimeframedata",
      startDateTime,
      endDateTime,
      {
        model: "Ephemeris",
        column_names: "target_alt",
        filter_column_names: "target_name",
        filter_values: "sun",
      }
    );

    // process data
    solarChargeRateData =
      changeBinnedTimeSeriesUTCTimeToLocalTime(solarChargeRateData);
    solarChargeRateData = ensureYKeyPresence(solarChargeRateData, [
      "solar_charge_rate",
    ]);
    solarChargeRateData = trimNullEdges(solarChargeRateData, [
      "solar_charge_rate",
    ]);

    solarAltitudeData = changeTimeSeriesUTCTimeToLocalTime(solarAltitudeData);
    solarAltitudeData = ensureYKeyPresence(solarAltitudeData, ["target_alt"]);
    solarAltitudeData = trimNullEdges(solarAltitudeData, ["target_alt"]);

    const chargeXlims = getXLims(getBinnedXValues(solarChargeRateData));
    const altitudeXLims = getXLims(
      solarAltitudeData.map((row) => row.datetime_utc)
    );
    const xLims = getWidestXLimits([chargeXlims, altitudeXLims]);

    return [[solarChargeRateData, solarAltitudeData], xLims];
  }

  // set up the initial data and axes
  const [initialData, xLims] = await getData();

  const solarChargeRateGraph = new DashboardTimeLineGraph(
    "#solar-charge-sun-alt-graph",
    "Time",
    "Sun Altitude [\u00B0]",
    "Solar Charge Rate [W]"
  );

  // set the graph parameters for consistency in initialization & refresh
  function makeBarGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[0],
      "time_bin_edges",
      "solar_charge_rate",
      {
        secondAxis: true,
        transition: true,
        staticYlims: [0, 2500],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }
  function makeLineGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[1],
      "datetime_utc",
      "target_alt",
      {
        secondAxis: false,
        transition: true,
        staticYlims: [-90, 90],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }

  // populate the graphs, saving the names of them
  const barAxesKey = solarChargeRateGraph.makeBarGraph(
    ...makeBarGraphParams(initialData, true, xLims)
  );
  const lineAxesKey = solarChargeRateGraph.makeLineGraph(
    ...makeLineGraphParams(initialData, true, xLims)
  );
  solarChargeRateGraph.showLoading();

  // define the refresh function
  async function refresh() {
    solarChargeRateGraph.showLoading();
    const [newData, xLims] = await getData();

    // updateData has the same format as make*Graph, but with the key as param 1
    solarChargeRateGraph.updateData(
      barAxesKey,
      ...makeBarGraphParams(newData, false, xLims)
    );
    solarChargeRateGraph.updateData(
      lineAxesKey,
      ...makeLineGraphParams(newData, false, xLims)
    );
  }

  return refresh;
}
